import {UnitStatusEnum} from '@core/modules/rest/consent/enums/unit-status.enum';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';
import {InformationUnitModel} from '@core/modules/rest/informationunit/response/information-unit.model';
import {getTextBasedOnLanguage, LanguageEnum} from '@core/enums/language.enum';
import {LevelEnum} from '@core/modules/rest/indication/enums/level.enum';

export function createNewOutlineState(outline: OutlineChapterUnitBasicModel[], unitOrderId: number, initialUnitOrderIdList: number[],
                                      status: UnitStatusEnum, viewDuration: number, timeSkipped?: Date): OutlineChapterUnitBasicModel[] {
    if (initialUnitOrderIdList.length === 0 || initialUnitOrderIdList.includes(unitOrderId)) {
        return outline.map(chapter => {
            return {
                ...chapter,
                units: chapter.units.map(unit => {
                    if (unit.unitOrder === unitOrderId) {
                        return {
                            ...unit,
                            status: writeUnitStatus(unit.status, status),
                            viewDurationInMilliseconds: viewDuration,
                            skipConfirmationTime: timeSkipped
                        };
                    } else if (unit.children.length > 0) {
                        return {
                            ...unit,
                            children: markUnitStatusForChildren(unit.children, unitOrderId, status, viewDuration, timeSkipped)
                        };
                    }
                    return unit;
                })
            };
        });
    }
    return outline;
}

function markUnitStatusForChildren(units: OutlineUnitBasicModel[], unitOrderId: number, newStatus: any, viewDuration: number,
                                   timeSkipped?: Date): OutlineUnitBasicModel[] {
    return units.map(unit => {
        if (unit.unitOrder === unitOrderId) {
            return {
                ...unit,
                status: writeUnitStatus(unit.status, newStatus),
                viewDurationInMilliseconds: viewDuration,
                skipConfirmationTime: timeSkipped
            };
        } else if (unit.children.length > 0) {
            return {
                ...unit,
                children: markUnitStatusForChildren(unit.children, unitOrderId, newStatus, viewDuration, timeSkipped)
            };
        }
        return unit;
    });
}

export function addUnitsToOutlineState(outline: OutlineChapterUnitBasicModel[], unitOrderId: number, units: OutlineUnitBasicModel[]): OutlineChapterUnitBasicModel[] {
    return outline.map(chapter => {
        return {
            ...chapter,
            units: chapter.units.map(unit => {
                if (unit.unitOrder === unitOrderId) {
                    return {
                        ...unit,
                        goAhead: units[0].unitOrder,
                        children: units
                    };
                } else if (unit.children.length > 0) {
                    return {
                        ...unit,
                        children: checkMoreForChildren(unit.children, unitOrderId, units)
                    };
                }
                return unit;
            })
        };
    });
}

function checkMoreForChildren(children: OutlineUnitBasicModel[], unitOrderId: number, units: OutlineUnitBasicModel[]): OutlineUnitBasicModel[] | any[] {
    return children.map(unit => {
        if (unit.unitOrder === unitOrderId) {
            return {
                ...unit,
                goAhead: units[0].unitOrder,
                children: units
            };
        } else if (unit.children.length > 0) {
            return {
                ...unit,
                children: checkMoreForChildren(unit.children, unitOrderId, units)
            };
        }
        return unit;
    });
}

export function createNewOutlineStateWithSkippedUnits(outline: OutlineChapterUnitBasicModel[], unitsToMark: number[], initialUnitOrderIdList: number[],
                                                      viewDuration: number, timeSkipped: Date): OutlineChapterUnitBasicModel[] {
    for (const unitOrderId of unitsToMark) {
        outline = createNewOutlineState(outline, unitOrderId, initialUnitOrderIdList, UnitStatusEnum.SKIPPED, viewDuration, timeSkipped);
    }
    return outline;
}

function writeUnitStatus(lastStatus: UnitStatusEnum, newStatus: UnitStatusEnum): UnitStatusEnum {
    if (lastStatus !== UnitStatusEnum.UNDERSTOOD) {
        return newStatus;
    }
    return lastStatus;
}

export function crateNewUnit(unit: InformationUnitModel, indicationId: string, language: LanguageEnum): OutlineUnitBasicModel {
    return {
        indicationId,
        informationUnitId: unit.id,
        chapterUnitId: unit.id,
        unitLevel: LevelEnum.A,
        unitRelevance: LevelEnum.A,
        indicationTitleEn: '',
        indicationTitleDe: '',
        consentChapterTitle: '',
        informationUnitUniqueTitle: unit.titleUnique,
        informationUnitTitle: getTextBasedOnLanguage(language, unit.titleStandardEn, unit.titleStandardDe, unit.titleStandardSr),
        informationUnitInfo: getTextBasedOnLanguage(language, unit.infoStandardEn, unit.infoStandardDe, unit.infoStandardSr),
        readingTimeStandardDe: 0,
        readingTimeSimpleDe: 0,
        readingTimeEasyDe: 0,
        readingTimeStandardEn: 0,
        status: UnitStatusEnum.UNMARKED,
        hasChildren: false,
        firstChildOrderId: -1,
        unitOrderParent: -1,
        unitOrder: unit.externalId,
        children: [],
        skipTo: [],
        goAhead: -1,
        popups: [],
        viewDurationInMilliseconds: 0
    };
}

export function replaceUnitInfoInOutline(units: OutlineUnitBasicModel[], changedUnit: InformationUnitModel, language: LanguageEnum): OutlineUnitBasicModel[] {
    return units.map(unit => {
        if (unit.informationUnitId === changedUnit.id) {
            return {
                ...unit,
                informationUnitTitle: getTextBasedOnLanguage(language, changedUnit.titleStandardEn, changedUnit.titleStandardDe, changedUnit.titleStandardSr),
                informationUnitInfo: getTextBasedOnLanguage(language, changedUnit.infoStandardEn, changedUnit.infoStandardDe, changedUnit.infoStandardSr)
            };
        } else {
            if (unit.children.length > 0) {
                return {
                    ...unit,
                    children: replaceUnitInfoInChildren(unit.children, changedUnit, language)
                };
            }
            return unit;
        }
    });
}


function replaceUnitInfoInChildren(children: OutlineUnitBasicModel[], changedUnit: InformationUnitModel, language: LanguageEnum): OutlineUnitBasicModel[] {
    return children.map(child => {
        if (child.informationUnitId === changedUnit.id) {
            return {
                ...child,
                informationUnitTitle: getTextBasedOnLanguage(language, changedUnit.titleStandardEn, changedUnit.titleStandardDe, changedUnit.titleStandardSr),
                informationUnitInfo: getTextBasedOnLanguage(language, changedUnit.infoStandardEn, changedUnit.infoStandardDe, changedUnit.infoStandardSr)
            };
        } else {
            if (child.children.length > 0) {
                return {
                    ...child,
                    children: replaceUnitInfoInChildren(child.children, changedUnit, language)
                };
            }
            return child;
        }
    });
}

export function populateFlatList(data: OutlineChapterUnitBasicModel[]): OutlineUnitBasicModel[] {
    const unitsFlat: OutlineUnitBasicModel[] = [];
    data?.forEach(chapter => chapter.units?.forEach(unit => {
        unitsFlat.push(unit);
        if (unit.children.length > 0) {
            getUnitChildren(unit, unitsFlat);
        }
    }));
    return unitsFlat;
}

function getUnitChildren(unit: OutlineUnitBasicModel, unitsFlat: OutlineUnitBasicModel[]): void {
    unit.children.forEach(item => {
        unitsFlat.push(item);
        if (unit.children.length > 0) {
            getUnitChildren(item, unitsFlat);
        }
    });
}
